import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useInvites } from '../hooks/useInvites';
import { Avatar } from './Avatar';
import { playSfx } from '../lib/sound';
import { haptic } from '../lib/haptic';
import { useSettings } from '../hooks/useSettings';

const AUTO_DISMISS_MS = 20_000;

/** Renders in-app invite toasts as the game-invite analog of an OS push.
 *  Listens to the socket for `invite:received` via useInvites; each toast
 *  offers Join / Dismiss and auto-fades after 20s if untouched. */
export function InviteToasts() {
  const invites = useInvites((s) => s.invites);
  const dismiss = useInvites((s) => s.dismiss);
  const nav = useNavigate();
  const sound = useSettings((s) => s.sound);
  const hapticOn = useSettings((s) => s.haptic);

  // Chime only for invites we haven't seen before, and give each toast its
  // own auto-dismiss timer. A single timer keyed on the whole array would be
  // reset by ANY list change — replaying the chime on manual dismissal and
  // letting older toasts outlive their 20s.
  const seenRef = useRef<Set<string>>(new Set());
  const timersRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    const timers = timersRef.current;
    let isNew = false;
    for (const i of invites) {
      if (seenRef.current.has(i.id)) continue;
      seenRef.current.add(i.id);
      isNew = true;
      timers.set(i.id, window.setTimeout(() => {
        timers.delete(i.id);
        dismiss(i.id);
      }, AUTO_DISMISS_MS));
    }
    if (isNew) {
      playSfx('chime', sound);
      haptic('success', hapticOn);
    }
    // Drop timers for toasts dismissed by the user.
    const live = new Set(invites.map((i) => i.id));
    for (const [id, t] of timers) {
      if (!live.has(id)) {
        window.clearTimeout(t);
        timers.delete(id);
      }
    }
  }, [invites, dismiss, sound, hapticOn]);
  useEffect(() => () => {
    for (const t of timersRef.current.values()) window.clearTimeout(t);
  }, []);

  return (
    <div
      className="fixed left-0 right-0 z-50 flex justify-center pointer-events-none px-3"
      style={{ top: 'calc(env(safe-area-inset-top) + 72px)' }}
    >
      <div className="w-full max-w-md flex flex-col gap-2">
        <AnimatePresence initial={false}>
          {invites.map((i) => (
            <motion.div
              key={i.id}
              layout
              initial={{ opacity: 0, y: -16, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -12, scale: 0.95, transition: { duration: 0.18 } }}
              transition={{ type: 'spring', stiffness: 320, damping: 26 }}
              className="pointer-events-auto panel p-3 flex items-center gap-3 border border-accent/40 shadow-lg"
            >
              <Avatar src={i.from.avatar} name={i.from.name} size={36} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate">
                  {i.from.name}
                  <span className="text-ink-200/80 font-normal"> invited you</span>
                </div>
                <div className="text-[11px] text-ink-200/70">Room {i.code}</div>
              </div>
              <button
                className="btn-ghost !py-1.5 !px-3 text-xs"
                onClick={() => dismiss(i.id)}
              >
                Dismiss
              </button>
              <button
                className="btn-primary !py-1.5 !px-3 text-xs"
                onClick={() => {
                  dismiss(i.id);
                  nav(`/g/${i.code}`);
                }}
              >
                Join
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
